import _ from 'lodash';
import React from 'react';
import useSWR from 'swr';
import { swrFetcher } from 'utils/api';

const { createContext, useContext, useState, useEffect } = React;

export const AppLaunchContext = createContext({});
AppLaunchContext.displayName = 'AppLaunch';

export const AppLaunchProvider = (props) => {
  const { children } = props;

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    '/api/v2/appLaunch',
    (url) => swrFetcher(url)
  );
  const { data: monthlyRanking } = useSWR(
    '/api/v4/monthlyRanking/tree',
    (url) => swrFetcher(url)
  );
  const productFacts = _.get(data, 'product_facts', []) || [];
  const reviewTags = _.get(data, 'review_tags', []) || [];
  const reviewOptions = _.get(data, 'review_options', {}) || {};
  const categoryTree = _.get(data, 'category_tree', []) || {};
  const searchSkinProblemKeywords =
    _.get(data, 'search_skin_problem_keywords', []) || [];
  const notifications = _.get(data, 'notifications', []) || [];
  const activityTags = _.get(data, 'activity_tags', []) || [];
  const campaigns = _.get(data, 'campaigns', []) || [];
  const factorTypes = _.get(data, 'factor_types', []) || [];
  const stores = _.get(data, 'stores', []) || [];
  const productOrigins = _.get(data, 'product_origins', []) || [];
  const ingredientSafeties = _.get(data, 'ingredient_safeties', []) || [];
  const ingredientFeatures = _.get(data, 'ingredient_features', []) || [];
  const contentCounts = _.get(data, 'content_counts', null) || {
    product_count: 0,
    review_count: 0,
    category_count: 0,
  };
  const reviewSources = _.get(data, 'review_sources', []) || [];

  const firstCategoryMap = _.reduce(
    categoryTree,
    (acc, firstCategory) => {
      acc[firstCategory.id] = firstCategory;
      return acc;
    },
    {}
  );

  const secondCategories = _.reduce(
    categoryTree,
    (acc, firstCategory) => {
      return acc.concat(firstCategory.second_categories);
    },
    []
  );

  const secondCategoryMap = _.reduce(
    categoryTree,
    (acc, firstCategory) => {
      const secondCategories = firstCategory.second_categories;
      _.forEach(secondCategories, (secondCategory) => {
        acc[secondCategory.id] = secondCategory;
      });
      return acc;
    },
    {}
  );

  const activityTagMap = _.reduce(
    activityTags,
    (acc, tag) => {
      acc[tag.id] = tag;
      return acc;
    },
    {}
  );

  const campaignMap = _.reduce(
    campaigns,
    (acc, campaign) => {
      acc[campaign.id] = campaign;
      return acc;
    },
    {}
  );

  const factors = _.reduce(
    factorTypes,
    (acc, factorType) => {
      return acc.concat(factorType.factors);
    },
    []
  );

  const factorMap = _.reduce(
    factors,
    (acc, factorType) => {
      acc[factorType.id] = factorType;
      return acc;
    },
    {}
  );

  const storeMap = _.reduce(
    stores,
    (acc, store) => {
      acc[store.id] = store;
      return acc;
    },
    {}
  );

  const productOriginMap = _.reduce(
    productOrigins,
    (acc, origin) => {
      acc[origin.id] = origin;
      return acc;
    },
    {}
  );

  const ingredientSafetyMap = _.keyBy(ingredientSafeties, 'id');
  const ingredientFeatureMap = _.keyBy(ingredientFeatures, 'id');
  const reviewSourceMap = _.keyBy(reviewSources, 'id');
  const reviewTagMap = _.keyBy(reviewTags, 'id');

  const getMonthlyRanks = (productId) => {
    if (
      !_.isEmpty(categoryTree) &&
      monthlyRanking &&
      !_.isEmpty(monthlyRanking.ranking)
    ) {
      // 2025年10月
      const [year, month] = monthlyRanking.starts_at.split('-');
      const dateStr = `${year}年${_.toNumber(month)}月`;
      let categoryRank = _.reduce(
        monthlyRanking.ranking,
        (acc, item) => {
          const { category, ranks } = item;
          const rank = _.indexOf(ranks, productId);
          if (rank !== -1) {
            acc.push({
              category,
              rank: rank + 1,
            });
          }
          return acc;
        },
        []
      );
      categoryRank = _.sortBy(categoryRank, 'rank');
      if (!_.isEmpty(categoryRank)) {
        return categoryRank.map((rankInfo) => {
          const category = rankInfo.category;
          return {
            year,
            month,
            date: dateStr,
            category: secondCategoryMap[category]
              ? secondCategoryMap[category].name
              : '', //secondCategoryMap[category].name, //firstRank.category,
            categoryId: category,
            rank: rankInfo.rank,
            // text: `${dateStr} ${secondCategoryMap[category].name}第${rankInfo.rank}位`, //2025年10月 面膜類第3位
          };
        });
      }
    }
    return null;
  };

  return (
    <AppLaunchContext.Provider
      value={{
        isLoading,
        categoryTree,
        secondCategories,
        searchSkinProblemKeywords,
        firstCategoryMap,
        secondCategoryMap,
        activityTagMap,
        factorTypes,
        factorMap,
        reviewOptions,
        reviewSources,
        reviewSourceMap,
        reviewTags,
        reviewTagMap,
        campaignMap,
        stores,
        storeMap,
        productFacts,
        productOrigins,
        productOriginMap,
        notifications,
        ingredientSafeties,
        ingredientSafetyMap,
        ingredientFeatures,
        ingredientFeatureMap,
        contentCounts,
        getMonthlyRanks,
      }}
    >
      {children}
    </AppLaunchContext.Provider>
  );
};

export const useAppLaunch = () => {
  return useContext(AppLaunchContext);
};
